import { createReducer } from '@reduxjs/toolkit';
import { CurrentReceiptData, LoyaltyPageState } from './types';
import * as actions from './actions';
import { AppState } from '../../global/types';

const initialState: LoyaltyPageState = {
  currentReceipt: {
    data: null,
    lastUpdate: null,
    loading: false,
    error: null,
  },
  chosenReceipt: {
    data: null,
  },
  appliedBonuses: {
    loading: false,
    error: null,
    result: null,
  },
  loyaltyOrder: {
    loading: false,
    error: null,
    result: null,
  },
  clientInfo: {
    result: null,
    loading: false,
    error: null,
  },
  pdfLoyaltyReport: {
    data: null,
    loading: false,
    error: null,
  },
};

const reducer = createReducer(initialState, {
  [actions.getCurrentReceipt.REQUEST]: (state) => {
    state.currentReceipt.loading = true;
  },
  [actions.getCurrentReceipt.SUCCESS]: (state, action) => {
    state.currentReceipt.loading = false;
    state.currentReceipt.error = null;

    const chosenReceipt = action.payload.checks.find(
      (item: CurrentReceiptData) =>
        item.doc_id === state.chosenReceipt.data?.doc_id
    );

    const stopUpdate =
      !!state.loyaltyOrder.result &&
      action.payload.checks.find(
        (item: CurrentReceiptData) => item.doc_id === state.loyaltyOrder.result
      )?.items?.length > 0;

    if (state.clientInfo.result && !stopUpdate) {
      state.currentReceipt.lastUpdate = new Date().toLocaleString();
      state.currentReceipt.data = action.payload.checks;
      state.chosenReceipt.data = chosenReceipt ?? null;
    }
  },
  [actions.getCurrentReceipt.ERROR]: (state, action) => {
    state.currentReceipt.loading = false;
    state.currentReceipt.lastUpdate = new Date().toLocaleString();
    state.currentReceipt.data = null;
    state.currentReceipt.error = action.payload;
  },
  [actions.setChosenReceipt.REQUEST]: (state, action) => {
    state.chosenReceipt.data = action.payload;
  },
  [actions.applyBonuses.REQUEST]: (state) => {
    state.appliedBonuses.loading = true;
    state.appliedBonuses.error = null;
    state.appliedBonuses.result = null;
    state.loyaltyOrder.error = null;
    state.loyaltyOrder.result = null;
  },
  [actions.applyBonuses.SUCCESS]: (state, action) => {
    state.appliedBonuses.loading = false;
    state.appliedBonuses.result = action.payload;
  },
  [actions.applyBonuses.ERROR]: (state, action) => {
    state.appliedBonuses.loading = false;
    state.appliedBonuses.error = action.payload;
  },
  [actions.getClientInfo.REQUEST]: (state) => {
    state.clientInfo.loading = true;
  },
  [actions.getClientInfo.SUCCESS]: (state, action) => {
    state.clientInfo.loading = false;
    state.clientInfo.result = action.payload;
  },
  [actions.getClientInfo.ERROR]: (state, action) => {
    state.clientInfo.loading = false;
    state.clientInfo.error = action.payload;
  },
  [actions.submitLoyaltyOrder.REQUEST]: (state) => {
    state.loyaltyOrder.loading = true;
    state.loyaltyOrder.error = null;
    state.loyaltyOrder.result = null;
  },
  [actions.submitLoyaltyOrder.SUCCESS]: (state, action) => {
    state.loyaltyOrder.loading = false;
    state.loyaltyOrder.result = action.payload;
  },
  [actions.submitLoyaltyOrder.ERROR]: (state, action) => {
    state.loyaltyOrder.loading = false;
    state.loyaltyOrder.error = action.payload;
  },
  [actions.resetClientInfo.type]: (state) => {
    state.clientInfo.loading = false;
    state.clientInfo.error = null;
    state.clientInfo.result = null;
  },
  [actions.resetCurrentReceipt.type]: (state) => {
    state.currentReceipt.loading = false;
    state.currentReceipt.error = null;
    state.currentReceipt.data = null;
    state.currentReceipt.lastUpdate = null;
    state.loyaltyOrder.result = null;
  },
  [actions.resetApplyBonuses.type]: (state) => {
    state.appliedBonuses.loading = false;
    state.appliedBonuses.error = null;
    state.appliedBonuses.result = null;
  },
  [actions.resetSubmitOrder.type]: (state) => {
    state.loyaltyOrder.loading = false;
    state.loyaltyOrder.error = null;
    state.loyaltyOrder.result = null;
  },
  [actions.generateLoyaltyReport.REQUEST]: (state) => {
    state.pdfLoyaltyReport.loading = true;
  },
  [actions.generateLoyaltyReport.SUCCESS]: (state, action) => {
    state.pdfLoyaltyReport.loading = false;
    state.pdfLoyaltyReport.data = action.payload;
  },
  [actions.generateLoyaltyReport.ERROR]: (state, action) => {
    state.pdfLoyaltyReport.loading = false;
    state.pdfLoyaltyReport.error = action.payload;
  },
});

const currentReceipt = (state: AppState) =>
  state.loyaltyPageReducer.currentReceipt.data;
const currentReceiptLoading = (state: AppState) =>
  state.loyaltyPageReducer.currentReceipt.loading;
const currentReceiptError = (state: AppState) =>
  state.loyaltyPageReducer.currentReceipt.error;
const chosenReceipt = (state: AppState) =>
  state.loyaltyPageReducer.chosenReceipt.data;
const lastUpdateTime = (state: AppState) =>
  state.loyaltyPageReducer.currentReceipt.lastUpdate;
const appliedBonuses = (state: AppState) =>
  state.loyaltyPageReducer.appliedBonuses.result;
const appliedBonusesLoading = (state: AppState) =>
  state.loyaltyPageReducer.appliedBonuses.loading;
const appliedBonusesError = (state: AppState) =>
  state.loyaltyPageReducer.appliedBonuses.error;
const clientInfo = (state: AppState) =>
  state.loyaltyPageReducer.clientInfo.result;
const clientInfoLoading = (state: AppState) =>
  state.loyaltyPageReducer.clientInfo.loading;
const resetClientInfo = (state: AppState) =>
  state.loyaltyPageReducer.clientInfo.result;
const submitOrderError = (state: AppState) =>
  state.loyaltyPageReducer.loyaltyOrder.error;
const submitOrderResult = (state: AppState) =>
  state.loyaltyPageReducer.loyaltyOrder.result;
const submitOrderLoading = (state: AppState) =>
  state.loyaltyPageReducer.loyaltyOrder.loading;
const resetSubmitOrder = (state: AppState) =>
  state.loyaltyPageReducer.loyaltyOrder.result;
const resetApplyBonuses = (state: AppState) =>
  state.loyaltyPageReducer.appliedBonuses.result;
const loyaltyPdf = (state: AppState) =>
  state.loyaltyPageReducer.pdfLoyaltyReport.data;

const selectors = {
  currentReceipt,
  currentReceiptLoading,
  currentReceiptError,
  chosenReceipt,
  lastUpdateTime,
  appliedBonuses,
  appliedBonusesLoading,
  appliedBonusesError,
  clientInfo,
  clientInfoLoading,
  resetClientInfo,
  submitOrderError,
  submitOrderResult,
  submitOrderLoading,
  resetSubmitOrder,
  resetApplyBonuses,
  loyaltyPdf,
};

export { selectors };
export default reducer;
